<template>
  <div data-testid="page-speakers">
    <AlertBox v-if="!canViewSpeakers" type="warning" class="mt-5">
      {{ t('PartnerCompany.noPartnerCompany') }}
    </AlertBox>
    <RouterView
      v-if="canViewSpeakers && result?.speakers"
      :speakers="result?.speakers.nodes"
      :page-info="result?.speakers.pageInfo"
      :me="props.me"
      :loading="loading"
      @reload-speakers="refetch"
      @update:page="updatePage"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { useSpeakersQuery, type MeQuery } from '@/gql/myomr'
import { useRoleRedirect } from '@/services/roleRedirect'
import { $can } from '@/services/roles'

const DEFAULT_PER_PAGE = 20

useRoleRedirect('viewSpeakers')

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const canViewSpeakers = computed(() => {
  return $can('viewSpeakers', { for: 'User' })
})

const props = defineProps<{
  me: MeQuery['me']
}>()

const currentPage = computed(() => positiveInteger(route.query.page) ?? 1)
const offset = computed(() => (currentPage.value - 1) * DEFAULT_PER_PAGE)

const { loading, refetch, result } = useSpeakersQuery(
  () => ({
    limit: DEFAULT_PER_PAGE,
    offset: offset.value,
  }),
  {
    errorPolicy: 'all',
  },
)

const pageInfo = computed(() => result.value?.speakers.pageInfo)

watch(pageInfo, (newPageInfo) => {
  if (!newPageInfo?.pagesCount || currentPage.value <= newPageInfo.pagesCount) return
  updatePage(newPageInfo.pagesCount)
})

function updatePage(page: number) {
  router.push({
    query: {
      ...route.query,
      page: page === 1 ? undefined : String(page),
    },
  })
}

function positiveInteger(value: unknown) {
  const parsedValue = Number(value)

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : undefined
}
</script>
